import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface SavedSearch {
  id: string;
  name: string;
  make: string | null;
  model: string | null;
  year_min: number;
  year_max: number;
  price_min: number;
  price_max: number;
  condition: string | null;
  category: string | null;
  location: string | null;
  radius: number;
  notifications_enabled: boolean;
  created_at: string;
}

interface SavedSearchesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadSearch: (search: SavedSearch) => void;
}

export default function SavedSearchesPanel({
  isOpen,
  onClose,
  onLoadSearch,
}: SavedSearchesPanelProps) {
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadSavedSearches();
    }
  }, [isOpen]);

  const loadSavedSearches = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setSearches([]);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('saved_searches')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSearches(data || []);
    } catch (err) {
      console.error('Error loading saved searches:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('saved_searches')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSearches(searches.filter(s => s.id !== id));
    } catch (err) {
      console.error('Error deleting search:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleNotifications = async (id: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from('saved_searches')
        .update({ notifications_enabled: !currentValue })
        .eq('id', id);

      if (error) throw error;
      
      setSearches(searches.map(s => 
        s.id === id ? { ...s, notifications_enabled: !currentValue } : s
      ));
    } catch (err) {
      console.error('Error updating notifications:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Saved Searches</h2>
              <p className="text-gray-600 mt-1">
                Manage your saved vehicle searches and notifications
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
            </div>
          ) : searches.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-bookmark-line text-3xl text-gray-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No saved searches yet</h3>
              <p className="text-gray-600">
                Save your search criteria to get notified about new vehicles
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {searches.map((search) => (
                <div
                  key={search.id}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-bold text-gray-900">{search.name}</h3>
                        {search.notifications_enabled && (
                          <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full flex items-center gap-1 whitespace-nowrap">
                            <i className="ri-notification-3-line"></i>
                            Notifications On
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mb-3">
                        {search.make && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                            {search.make}
                          </span>
                        )}
                        {search.model && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                            {search.model}
                          </span>
                        )}
                        {search.category && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                            {search.category}
                          </span>
                        )}
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {search.year_min} - {search.year_max}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          ${search.price_min.toLocaleString()} - ${search.price_max.toLocaleString()}
                        </span>
                        {search.location && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full flex items-center gap-1">
                            <i className="ri-map-pin-line"></i>
                            {search.location} ({search.radius === 1000 ? 'Any' : `${search.radius}mi`})
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-500">
                        Saved {new Date(search.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => {
                          onLoadSearch(search);
                          onClose();
                        }}
                        className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-search-line mr-1"></i>
                        Load
                      </button>
                      <button
                        onClick={() => handleToggleNotifications(search.id, search.notifications_enabled)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                        title={search.notifications_enabled ? 'Disable notifications' : 'Enable notifications'}
                      >
                        <i className={`${search.notifications_enabled ? 'ri-notification-off-line' : 'ri-notification-3-line'} mr-1`}></i>
                        {search.notifications_enabled ? 'Mute' : 'Notify'}
                      </button>
                      <button
                        onClick={() => handleDelete(search.id)}
                        disabled={deletingId === search.id}
                        className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-50"
                      >
                        {deletingId === search.id ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : (
                          <i className="ri-delete-bin-line"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
